import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Gauge, ChevronRight, Copy, Check, CircleCheck } from "lucide-react";

const CODECS = {
  "G.711 (80 kbps)": 80,
  "G.722 (80 kbps)": 80,
  "G.729 (24 kbps)": 24,
  "Opus (~40 kbps)": 40,
};

export default function Qos() {
  const [iface, setIface] = useState("GigabitEthernet0/0/1");
  const [wanMbps, setWanMbps] = useState(10);
  const [calls, setCalls] = useState(20);
  const [codec, setCodec] = useState("G.711 (80 kbps)");
  const [videoPct, setVideoPct] = useState(15);
  const [sigPct, setSigPct] = useState(5);
  const [copied, setCopied] = useState(false);

  const calc = useMemo(() => {
    const wanKbps = Math.max(1, Number(wanMbps) || 0) * 1000;
    const codecKbps = CODECS[codec] || 80;
    const voiceKbps = (Number(calls) || 0) * codecKbps;
    const voicePct = Math.min(100, Math.round((voiceKbps / wanKbps) * 100));
    const overSubscribed = voicePct + Number(videoPct) + Number(sigPct) > 90;
    return { wanKbps, codecKbps, voiceKbps, voicePct, overSubscribed };
  }, [wanMbps, calls, codec, videoPct, sigPct]);

  const config = useMemo(() => {
    const { voiceKbps } = calc;
    return `! ===== QoS Policy — generated by UC Engineer Pro =====
! WAN: ${wanMbps} Mbps on ${iface} | ${calls} concurrent ${codec} calls

class-map match-any VOICE
 match dscp ef
class-map match-any VIDEO
 match dscp af41
class-map match-any SIGNALING
 match dscp cs3
 match dscp af31

policy-map WAN-EDGE-OUT
 class VOICE
  priority ${voiceKbps}
 class VIDEO
  bandwidth remaining percent ${videoPct}
 class SIGNALING
  bandwidth remaining percent ${sigPct}
 class class-default
  fair-queue
  random-detect dscp-based

interface ${iface}
 service-policy output WAN-EDGE-OUT

! ---- Trust boundary at the access layer (LAN switch) ----
! interface range <access-ports>
!  mls qos trust device cisco-phone
!  mls qos trust dscp`;
  }, [iface, wanMbps, calls, codec, videoPct, sigPct, calc]);

  const validation = [
    { cmd: "show policy-map interface " + iface, good: "Voice class shows priority with 0 (or minimal) drops" },
    { cmd: "show mls qos interface " + iface, good: "Trust state = trust dscp on WAN uplink" },
    { cmd: "show policy-map interface | inc drop", good: "No output drops in the VOICE priority queue" },
    { cmd: "show call active voice brief", good: "Active RTP streams marked DSCP EF (46) end-to-end" },
  ];

  const copy = () => {
    navigator.clipboard.writeText(config);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
    toast.success("QoS config copied");
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> qos <ChevronRight className="h-3 w-3" /> builder
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">
          <Gauge className="h-4 w-4" /> QoS Policy Builder
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">WAN/LAN QoS Policy</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Size an LLQ policy for your WAN edge and generate ready-to-paste Cisco IOS config — priority queue for
          voice (DSCP EF), bandwidth for video (AF41) and signaling (CS3), with a validation checklist.
        </p>

        <div className="grid lg:grid-cols-2 gap-5 mt-6">
          {/* Inputs */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-5 space-y-4">
            <Field label="WAN interface">
              <Input data-testid="qos-iface" value={iface} onChange={(e) => setIface(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-sm font-mono" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="WAN speed (Mbps)">
                <Input data-testid="qos-wan" type="number" value={wanMbps} onChange={(e) => setWanMbps(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-sm" />
              </Field>
              <Field label="Concurrent calls">
                <Input data-testid="qos-calls" type="number" value={calls} onChange={(e) => setCalls(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-sm" />
              </Field>
            </div>
            <Field label="Voice codec">
              <Select value={codec} onValueChange={setCodec}>
                <SelectTrigger data-testid="qos-codec" className="h-9 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">{Object.keys(CODECS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Video (remaining %)">
                <Input data-testid="qos-video" type="number" value={videoPct} onChange={(e) => setVideoPct(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-sm" />
              </Field>
              <Field label="Signaling (remaining %)">
                <Input data-testid="qos-sig" type="number" value={sigPct} onChange={(e) => setSigPct(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-sm" />
              </Field>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-[13px]" data-testid="qos-summary">
              <Row k="Voice bandwidth (LLQ priority)" v={`${calc.voiceKbps} kbps`} />
              <Row k="Voice as % of WAN" v={`${calc.voicePct}%`} tone={calc.voicePct > 33 ? "amber" : "default"} />
            </div>
            {calc.overSubscribed && (
              <div data-testid="qos-oversub-warning" className="text-[12px] text-amber-400">
                ⚠ Voice + video + signaling exceeds ~90% of the link — reduce call count or codec, or upgrade the WAN.
              </div>
            )}
          </div>

          {/* Output */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Generated config</span>
              <button onClick={copy} data-testid="qos-copy-btn" className="flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-emerald-600/50 hover:text-emerald-400">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />} Copy
              </button>
            </div>
            <pre data-testid="qos-config" className="rounded-lg border border-zinc-800 bg-black p-4 overflow-x-auto max-h-[420px] overflow-y-auto"><code className="font-mono text-[12px] leading-relaxed text-emerald-300 whitespace-pre">{config}</code></pre>
          </div>
        </div>

        {/* Validation */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Validation Checklist</div>
          <div className="space-y-2" data-testid="qos-validation">
            {validation.map((v, i) => (
              <div key={i} className="flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-900 p-3">
                <CircleCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-mono text-[13px] text-white">{v.cmd}</div>
                  <div className="text-[12px] text-zinc-400 mt-0.5"><span className="text-zinc-600">Good:</span> {v.good}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1.5">{label}</label>
    {children}
  </div>
);

const Row = ({ k, v, tone = "default" }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-zinc-400">{k}</span>
    <span className={`font-mono font-semibold ${tone === "amber" ? "text-amber-400" : "text-white"}`}>{v}</span>
  </div>
);
